// The dashboard half of Phone Record Processing: everything shown once carrier records have
// been read. Kept apart from the upload page so the Case Analyzer desktop app can show records
// it has already read with exactly the same views (it passes a Close button and can open on one
// response, e.g. "tmobile:5550100003").

import { useState, useMemo, useCallback } from 'react';
import moment from 'moment-timezone';
import { GeneratedMark, GeneratedLegend } from '../ReviewAid';
import PhoneAnalysis from '../analysis/PhoneAnalysis';
import { fmtNum } from '../../utils/cdrRecords';

// ── Constants ─────────────────────────────────────────────────────────

export const CARRIERS = {
  tmobile: { label: 'T-Mobile', color: '#e20074', bg: '#fce4f0', border: '#f9a8d4' },
  att:     { label: 'AT&T',     color: '#0076b0', bg: '#e0f2fe', border: '#7dd3fc' },
  verizon: { label: 'Verizon',  color: '#cd040b', bg: '#fde8e9', border: '#fca5a5' },
};

const TYPE_COLORS = {
  voice:   { bg: '#dbeafe', color: '#1e40af', border: '#bfdbfe' },
  sms:     { bg: '#dcfce7', color: '#166534', border: '#bbf7d0' },
  mms:     { bg: '#f3e8ff', color: '#6b21a8', border: '#e9d5ff' },
  data:    { bg: '#fff7ed', color: '#9a3412', border: '#fed7aa' },
  unknown: { bg: '#f1f5f9', color: '#475569', border: '#e2e8f0' },
};

const HOURS = Array.from({ length: 24 }, (_, i) =>
  i === 0 ? '12a' : i < 12 ? `${i}a` : i === 12 ? '12p' : `${i - 12}p`
);
const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const PAGE_SIZE = 100;

// ── Time zones ────────────────────────────────────────────────────────
// Carrier returns usually arrive in UTC/GMT; investigators want to read them in
// a local zone. Every timestamp is stored as a naive wall-clock in the Date's
// UTC fields (all parsers normalize to this), then re-interpreted in the chosen
// SOURCE zone and converted to the DISPLAY zone for viewing.

const TIME_ZONES = [
  { v: 'UTC',                 label: 'UTC / GMT' },
  { v: 'America/New_York',    label: 'Eastern — New York' },
  { v: 'America/Chicago',     label: 'Central — Chicago' },
  { v: 'America/Denver',      label: 'Mountain — Denver' },
  { v: 'America/Phoenix',     label: 'Arizona (no DST)' },
  { v: 'America/Los_Angeles', label: 'Pacific — Los Angeles' },
  { v: 'America/Anchorage',   label: 'Alaska — Anchorage' },
  { v: 'Pacific/Honolulu',    label: 'Hawaii — Honolulu' },
];

function wallParts(ts) {
  return [ts.getUTCFullYear(), ts.getUTCMonth(), ts.getUTCDate(),
          ts.getUTCHours(), ts.getUTCMinutes(), ts.getUTCSeconds()];
}
function zoned(ts, srcZone, dispZone) {
  if (!ts) return null;
  return moment.tz(wallParts(ts), srcZone).tz(dispZone);
}
function fmtTs(ts, srcZone, dispZone) {
  const m = zoned(ts, srcZone, dispZone);
  return m ? m.format('M/D/YY h:mm:ss A') : '—';
}
function zoneAbbr(dispZone) {
  return moment.tz(dispZone).format('z');
}
// Reverses zoned(): a <input type="datetime-local"> value is a wall-clock
// reading in the display zone (what the table shows), so to compare it
// against record.timestamp it needs to go back through the same srcZone
// wall-clock-in-UTC-fields storage every parser uses.
function pivotToRecordTime(localValue, srcZone, dispZone) {
  if (!localValue) return null;
  const m = moment.tz(localValue, dispZone).tz(srcZone);
  if (!m.isValid()) return null;
  return new Date(Date.UTC(m.year(), m.month(), m.date(), m.hour(), m.minute(), m.second()));
}

// ── Analytics ─────────────────────────────────────────────────────────

function computeStats(records, srcZone = 'UTC', dispZone = 'UTC') {
  const byType = { voice: 0, sms: 0, mms: 0, data: 0, unknown: 0 };
  const byHour = Array(24).fill(0);
  const byDow  = Array(7).fill(0);
  const cMap   = {};

  records.forEach(r => {
    byType[r.type] = (byType[r.type] || 0) + 1;
    if (r.timestamp) {
      const m = zoned(r.timestamp, srcZone, dispZone);
      byHour[m.hour()]++;
      byDow[m.day()]++;
    }
    const k = r.contactNumber;
    if (k && r.type !== 'data') {
      if (!cMap[k]) cMap[k] = { num: k, count: 0, dur: 0, in: 0, out: 0, lastSeen: null };
      const c = cMap[k];
      c.count++;
      c.dur += r.durationSeconds || 0;
      if (r.direction === 'incoming') c.in++;
      if (r.direction === 'outgoing') c.out++;
      if (r.timestamp && (!c.lastSeen || r.timestamp > c.lastSeen)) c.lastSeen = r.timestamp;
    }
  });

  return {
    byType, byHour, byDow,
    contacts: Object.values(cMap).sort((a, b) => b.count - a.count),
    total: records.length,
  };
}

// Groups voice/SMS/MMS records by subject number (data sessions have no
// "other party" and are excluded), building each subject's own contact list
// with aggregate stats. Shared by the common-caller and dropped-call
// analyses below, which both start from "who did each subject talk to."
function buildSubjectContactStats(records) {
  const bySubject = new Map(); // subNum -> Map(contact -> stats)
  records.forEach(r => {
    if (!['voice', 'sms', 'mms'].includes(r.type)) return;
    const sub = r.subNum, contact = r.contactNumber;
    if (!sub || !contact) return;
    if (!bySubject.has(sub)) bySubject.set(sub, new Map());
    const cMap = bySubject.get(sub);
    if (!cMap.has(contact)) cMap.set(contact, { count: 0, in: 0, out: 0, dur: 0, first: null, last: null });
    const c = cMap.get(contact);
    c.count++;
    c.dur += r.durationSeconds || 0;
    if (r.direction === 'incoming') c.in++;
    if (r.direction === 'outgoing') c.out++;
    if (r.timestamp) {
      if (!c.first || r.timestamp < c.first) c.first = r.timestamp;
      if (!c.last  || r.timestamp > c.last)  c.last  = r.timestamp;
    }
  });
  return bySubject;
}

// subNum -> Map(contact -> stats) becomes contact -> Map(subNum -> stats).
function invertBySubject(bySubject) {
  const byContact = new Map();
  bySubject.forEach((cMap, sub) => {
    cMap.forEach((stats, contact) => {
      if (!byContact.has(contact)) byContact.set(contact, new Map());
      byContact.get(contact).set(sub, stats);
    });
  });
  return byContact;
}

// Finds numbers that show up for two or more of the uploaded subjects, plus
// the case where one subject number directly contacted another.
function computeCommonCallers(records) {
  const bySubject = buildSubjectContactStats(records);
  const subjects = [...bySubject.keys()];
  const subjectSet = new Set(subjects);
  const byContact = invertBySubject(bySubject);

  const common = [];
  byContact.forEach((subMap, contact) => {
    if (subMap.size < 2) return;
    const perSubject = [...subMap.entries()]
      .map(([subNum, s]) => ({ subNum, ...s }))
      .sort((a, b) => b.count - a.count);
    const allTs = perSubject.flatMap(p => [p.first, p.last]).filter(Boolean);
    common.push({
      contact,
      isSubject: subjectSet.has(contact),
      subjectCount: subMap.size,
      perSubject,
      totalCount: perSubject.reduce((a, p) => a + p.count, 0),
      firstSeen: allTs.length ? new Date(Math.min(...allTs.map(t => t.getTime()))) : null,
      lastSeen:  allTs.length ? new Date(Math.max(...allTs.map(t => t.getTime()))) : null,
    });
  });

  common.sort((a, b) => b.subjectCount - a.subjectCount || b.totalCount - a.totalCount);
  return { subjects, common };
}

// Splits records at a transition window and looks for a phone-swap
// signature: a number common to 2+ subjects before the window that 2+ of
// those subjects individually stop contacting after it ("dropped"), paired
// against a number 2+ subjects individually start contacting after the
// window having never contacted it before ("new"). A dropped/new pair
// sharing 2+ of the same subjects is a likely swap — e.g. B and C both used
// to reach A at one number and both start reaching a new number instead.
// A subject with residual contact on the old number (or prior contact on
// the new one) doesn't disqualify the pattern for the others — see
// residualSubjects/priorSubjects on each entry.
//
// windowStartMs/windowEndMs bound a transition buffer (records inside are
// excluded from both sides) rather than a single hard instant — real
// rollouts across several people are rarely perfectly synchronized (e.g.
// Bob and Frank get told on day 1, Carol not until day 4), so a record
// landing in that gap shouldn't count as "still using the old number after
// the swap" for whoever hasn't transitioned yet. Passing windowEndMs equal
// to windowStartMs (or omitting it) collapses back to a single-instant
// pivot.
//
// Every dropped/new entry also carries how many subjects total had ANY
// contact with that number on its side (totalBefore/totalAfter) — raw
// subject counts alone don't convey confidence well once there are more
// than a couple of subjects in play: "2 of 2 subjects dropped this number"
// is much stronger evidence than "2 of 6". Swaps carry a Jaccard-style
// confidence score (overlap size over the union of both subject sets) for
// the same reason, and are ranked by it first.
function computeDroppedCallAnalysis(records, windowStartMs, windowEndMs) {
  const empty = { subjects: [], dropped: [], newCommon: [], swaps: [] };
  if (!windowStartMs) return empty;
  const winStart = windowStartMs;
  const winEnd = windowEndMs != null ? Math.max(windowEndMs, windowStartMs) : windowStartMs;

  const beforeRecs = [], afterRecs = [];
  records.forEach(r => {
    if (!r.timestamp) return;
    const t = r.timestamp.getTime();
    if (t < winStart) beforeRecs.push(r);
    else if (t >= winEnd) afterRecs.push(r);
    // else: falls inside the transition window — excluded from both sides
  });

  const beforeBySubject = buildSubjectContactStats(beforeRecs);
  const afterBySubject  = buildSubjectContactStats(afterRecs);
  const beforeByContact = invertBySubject(beforeBySubject);
  const afterByContact  = invertBySubject(afterBySubject);
  const subjects = [...new Set([...beforeBySubject.keys(), ...afterBySubject.keys()])];
  if (subjects.length < 2) return empty;

  // "Dropped" and "new" are judged per subject, not as an all-or-nothing
  // verdict for the whole group — one straggler still texting the old
  // number after the window doesn't erase the pattern for the subjects who
  // actually did stop. A contact only needs 2+ subjects to individually
  // show the drop/pickup for it to count; any others are kept as
  // informational context rather than disqualifying the entry.
  const dropped = [];
  beforeByContact.forEach((beforeSubMap, contact) => {
    if (beforeSubMap.size < 2) return; // was common before the window
    const afterSubMap = afterByContact.get(contact) || new Map();
    const droppedSubs = [...beforeSubMap.keys()].filter(s => !afterSubMap.has(s));
    if (droppedSubs.length < 2) return; // need 2+ subjects who actually stopped
    dropped.push({
      contact,
      subjects: droppedSubs,
      residualSubjects: [...beforeSubMap.keys()].filter(s => afterSubMap.has(s)),
      totalBefore: beforeSubMap.size,
      perSubject: droppedSubs.map(s => ({ subNum: s, ...beforeSubMap.get(s) })).sort((a, b) => b.count - a.count),
    });
  });
  dropped.sort((a, b) => b.subjects.length / b.totalBefore - a.subjects.length / a.totalBefore || b.subjects.length - a.subjects.length);

  const newCommon = [];
  afterByContact.forEach((afterSubMap, contact) => {
    if (afterSubMap.size < 2) return; // is common after the window
    const beforeSubMap = beforeByContact.get(contact) || new Map();
    const newSubs = [...afterSubMap.keys()].filter(s => !beforeSubMap.has(s));
    if (newSubs.length < 2) return; // need 2+ subjects who are genuinely new to it
    newCommon.push({
      contact,
      subjects: newSubs,
      priorSubjects: [...afterSubMap.keys()].filter(s => beforeSubMap.has(s)),
      totalAfter: afterSubMap.size,
      perSubject: newSubs.map(s => ({ subNum: s, ...afterSubMap.get(s) })).sort((a, b) => b.count - a.count),
    });
  });
  newCommon.sort((a, b) => b.subjects.length / b.totalAfter - a.subjects.length / a.totalAfter || b.subjects.length - a.subjects.length);

  const swaps = [];
  dropped.forEach(d => {
    newCommon.forEach(n => {
      const overlap = d.subjects.filter(s => n.subjects.includes(s));
      if (overlap.length < 2) return;
      // Confidence weighs against everyone touched by EITHER side, not just
      // the two already-filtered subject lists — a straggler who kept using
      // the old number (d.residualSubjects) or someone who already knew the
      // new one (n.priorSubjects) should dilute confidence even if they
      // don't show up in the overlap itself.
      const involved = new Set([...d.subjects, ...d.residualSubjects, ...n.subjects, ...n.priorSubjects]);
      const unionSize = involved.size;
      swaps.push({ dropped: d, added: n, overlap, unionSize, confidence: overlap.length / unionSize });
    });
  });
  swaps.sort((a, b) => b.confidence - a.confidence || b.overlap.length - a.overlap.length);

  return { subjects, dropped, newCommon, swaps };
}

// ── Helpers ───────────────────────────────────────────────────────────

function fmtDur(s) {
  if (!s) return '—';
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60), sec = s % 60;
  if (m >= 60) { const h = Math.floor(m / 60); return `${h}h ${m % 60}m`; }
  return sec ? `${m}m ${sec}s` : `${m}m`;
}

function fmtDateRange(min, max, srcZone = 'UTC', dispZone = 'UTC') {
  if (!min) return '';
  const f = d => zoned(d, srcZone, dispZone).format('MMM D, YYYY');
  const a = f(min), b = max ? f(max) : a;
  return a !== b ? `${a} – ${b}` : a;
}

// ── Bar Chart ─────────────────────────────────────────────────────────

function BarChart({ values, labels, color = '#818cf8', height = 60 }) {
  const max = Math.max(...values, 1);
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 1, height: height + 18 }}>
      {values.map((v, i) => (
        <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-end', height: '100%' }}>
          <div
            title={labels ? `${labels[i]}: ${v}` : String(v)}
            style={{ width: '100%', height: `${Math.max((v / max) * height, v > 0 ? 2 : 0)}px`, background: color, borderRadius: '2px 2px 0 0', minHeight: 0 }}
          />
          <div style={{ fontSize: 9, color: '#94a3b8', marginTop: 2, lineHeight: 1, textAlign: 'center', overflow: 'hidden' }}>
            {labels?.[i] || ''}
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Common Callers Panel ─────────────────────────────────────────────

function CommonCallersPanel({ data, names, srcZone, dispZone, onSelect }) {
  const { subjects, common } = data;
  const [search, setSearch]   = useState('');
  const [sortKey, setSortKey] = useState('subjectCount'); // 'subjectCount' | 'totalCount' | 'lastSeen'
  const [sortDir, setSortDir] = useState('desc');
  const [page, setPage]       = useState(0);

  const toggleSort = key => {
    setSortDir(d => (sortKey === key ? (d === 'desc' ? 'asc' : 'desc') : 'desc'));
    setSortKey(key);
    setPage(0);
  };

  const filtered = useMemo(() => {
    let rows = common;
    if (search) {
      const sn = search.replace(/\D/g, '');
      const sl = search.toLowerCase();
      rows = rows.filter(c => {
        const nameMatch = (names[c.contact] || '').toLowerCase().includes(sl);
        const numMatch  = sn ? c.contact.includes(sn) : false;
        return nameMatch || numMatch;
      });
    }
    const dir = sortDir === 'asc' ? 1 : -1;
    const key = sortKey === 'subjectCount' ? c => c.subjectCount
      : sortKey === 'totalCount' ? c => c.totalCount
      : c => c.lastSeen?.getTime() || 0;
    return [...rows].sort((a, b) => dir * (key(a) - key(b)) || (b.totalCount - a.totalCount));
  }, [common, search, sortKey, sortDir, names]);

  const pageRows   = filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);
  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);

  const sortArrow = key => sortKey === key ? (sortDir === 'desc' ? ' ▼' : ' ▲') : '';
  const thSortable = (key, label, align = 'left') => (
    <th onClick={() => toggleSort(key)} style={{ padding: '9px 12px', textAlign: align, fontWeight: 700, fontSize: 11, color: sortKey === key ? '#0f172a' : '#64748b', textTransform: 'uppercase', letterSpacing: '.05em', cursor: 'pointer', whiteSpace: 'nowrap', userSelect: 'none' }}>
      {label}{sortArrow(key)}
    </th>
  );

  return (
    <div style={{ flex: 1, overflowY: 'auto', padding: 24 }}>
      <div style={{ maxWidth: 1300, margin: '0 auto' }}>
        <div style={{ marginBottom: 14 }}>
          <h3 style={{ margin: '0 0 4px', fontSize: 15, fontWeight: 700, color: '#0f172a' }}>Common Caller Analysis<GeneratedMark /></h3>
          <p style={{ margin: 0, fontSize: 12.5, color: '#64748b' }}>
            {common.length} contact{common.length !== 1 ? 's' : ''} shared by 2 or more of the {subjects.length} numbers provided. Voice, SMS and MMS only.
          </p>
        </div>

        {common.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8', fontSize: 13, border: '1px dashed #e2e8f0', borderRadius: 10 }}>
            No contacts in common between the numbers provided.
          </div>
        ) : (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
              <input value={search} onChange={e => { setSearch(e.target.value); setPage(0); }} placeholder="Search number or name…"
                style={{ padding: '6px 10px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 12, width: 190, outline: 'none' }} />
              <span style={{ fontSize: 12, color: '#64748b' }}>
                Showing <span style={{ fontWeight: 600, color: '#0f172a' }}>{filtered.length.toLocaleString()}</span> of {common.length.toLocaleString()}
              </span>
              {totalPages > 1 && (
                <div style={{ marginLeft: 'auto', display: 'flex', gap: 6, alignItems: 'center' }}>
                  <button disabled={page === 0} onClick={() => setPage(p => p - 1)} style={{ padding: '3px 10px', border: '1px solid #e2e8f0', borderRadius: 4, fontSize: 12, cursor: page === 0 ? 'default' : 'pointer', background: '#fff', color: page === 0 ? '#cbd5e1' : '#0f172a' }}>←</button>
                  <span style={{ fontSize: 12, color: '#64748b' }}>{page + 1} / {totalPages}</span>
                  <button disabled={page === totalPages - 1} onClick={() => setPage(p => p + 1)} style={{ padding: '3px 10px', border: '1px solid #e2e8f0', borderRadius: 4, fontSize: 12, cursor: page === totalPages - 1 ? 'default' : 'pointer', background: '#fff', color: page === totalPages - 1 ? '#cbd5e1' : '#0f172a' }}>→</button>
                </div>
              )}
            </div>

            <div style={{ border: '1px solid #e2e8f0', borderRadius: 10, overflow: 'auto', maxHeight: 'calc(100vh - 260px)' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                  <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                    <th style={{ padding: '9px 12px', textAlign: 'left', fontWeight: 700, fontSize: 11, color: '#64748b', textTransform: 'uppercase', letterSpacing: '.05em' }}>Contact</th>
                    {thSortable('subjectCount', 'In Common', 'center')}
                    {subjects.map(s => (
                      <th key={s} title={fmtNum(s)} style={{ padding: '9px 10px', textAlign: 'center', fontWeight: 700, fontSize: 11, color: '#64748b', whiteSpace: 'nowrap' }}>
                        {names[s] || fmtNum(s)}
                      </th>
                    ))}
                    {thSortable('totalCount', 'Total', 'right')}
                    <th style={{ padding: '9px 12px', textAlign: 'left', fontWeight: 700, fontSize: 11, color: '#64748b', textTransform: 'uppercase', letterSpacing: '.05em', whiteSpace: 'nowrap' }}>First Seen</th>
                    {thSortable('lastSeen', 'Last Seen')}
                  </tr>
                </thead>
                <tbody>
                  {pageRows.map(c => {
                    const bySub = new Map(c.perSubject.map(p => [p.subNum, p]));
                    return (
                      <tr key={c.contact} onClick={() => onSelect(c.contact)} style={{ borderBottom: '1px solid #f1f5f9', cursor: 'pointer' }}
                          onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'} onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                        <td style={{ padding: '7px 12px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                            {c.isSubject && <span title="Direct contact between two of the provided numbers" style={{ width: 7, height: 7, borderRadius: '50%', background: '#dc2626', flexShrink: 0 }} />}
                            {names[c.contact] ? (
                              <div>
                                <div style={{ fontSize: 12, fontWeight: 700, color: '#0f172a' }}>{names[c.contact]}</div>
                                <div style={{ fontSize: 11, fontFamily: 'monospace', color: '#64748b' }}>{fmtNum(c.contact) || c.contact}</div>
                              </div>
                            ) : (
                              <span style={{ fontSize: 12, fontFamily: 'monospace', color: '#0f172a' }}>{fmtNum(c.contact) || c.contact}</span>
                            )}
                          </div>
                        </td>
                        <td style={{ padding: '7px 12px', textAlign: 'center' }}>
                          <span title={`In common with ${c.subjectCount} of ${subjects.length} provided numbers`}
                            style={{ fontSize: 11, fontWeight: 700, padding: '1px 8px', borderRadius: 20, background: '#eef2ff', color: '#4338ca', border: '1px solid #e0e7ff' }}>
                            {c.subjectCount}/{subjects.length}
                          </span>
                        </td>
                        {subjects.map(s => {
                          const p = bySub.get(s);
                          return (
                            <td key={s} style={{ padding: '7px 10px', textAlign: 'center', fontSize: 12, fontFamily: 'monospace', color: p ? '#0f172a' : '#cbd5e1' }}
                                title={p ? `${p.count} contact${p.count !== 1 ? 's' : ''} · ${p.out} out / ${p.in} in` : 'No contact'}>
                              {p ? p.count : '—'}
                            </td>
                          );
                        })}
                        <td style={{ padding: '7px 12px', textAlign: 'right', fontWeight: 700, color: '#0f172a' }}>{c.totalCount}</td>
                        <td style={{ padding: '7px 12px', fontSize: 12, color: '#475569', fontFamily: 'monospace', whiteSpace: 'nowrap' }}>{c.firstSeen ? fmtTs(c.firstSeen, srcZone, dispZone) : '—'}</td>
                        <td style={{ padding: '7px 12px', fontSize: 12, color: '#475569', fontFamily: 'monospace', whiteSpace: 'nowrap' }}>{c.lastSeen ? fmtTs(c.lastSeen, srcZone, dispZone) : '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ── Dropped Call (Number Swap) Panel ─────────────────────────────────

function DroppedNumberList({ items, matches, names, onSelect }) {
  if (!items.length) {
    return <div style={{ padding: 20, textAlign: 'center', color: '#94a3b8', fontSize: 12.5, border: '1px dashed #e2e8f0', borderRadius: 8 }}>None found.</div>;
  }
  return (
    <div style={{ border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'hidden' }}>
      {items.map((it, i) => {
        const match = matches.get(it.contact);
        const partner = match ? (match.dropped.contact === it.contact ? match.added.contact : match.dropped.contact) : null;
        const total = it.totalBefore ?? it.totalAfter;
        const leftover = it.residualSubjects?.length ? `${it.residualSubjects.length} still in occasional contact`
          : it.priorSubjects?.length ? `${it.priorSubjects.length} already had this number`
          : null;
        return (
          <div key={it.contact} onClick={() => onSelect(it.contact)}
            style={{ padding: '9px 12px', borderBottom: i < items.length - 1 ? '1px solid #f1f5f9' : 'none', cursor: 'pointer' }}
            onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'} onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              {names[it.contact] && <span style={{ fontSize: 12, fontWeight: 700, color: '#0f172a' }}>{names[it.contact]}</span>}
              <span style={{ fontFamily: 'monospace', fontSize: 13, color: names[it.contact] ? '#64748b' : '#0f172a' }}>{fmtNum(it.contact)}</span>
              <span title={`${it.subjects.length} of ${total} subjects who had any contact with this number`}
                style={{ fontSize: 10, fontWeight: 700, padding: '1px 7px', borderRadius: 20, background: '#f1f5f9', color: '#475569', border: '1px solid #e2e8f0' }}>
                {it.subjects.length}/{total}
              </span>
              {partner && (
                <span title="Best-matching swap partner by shared subjects" style={{ fontSize: 10, fontWeight: 700, padding: '1px 7px', borderRadius: 20, background: '#fef3c7', color: '#92400e', border: '1px solid #fde68a' }}>
                  ↔ {fmtNum(partner)}
                </span>
              )}
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 4 }}>
              {it.perSubject.map(p => (
                <span key={p.subNum} title={`${p.count} contact${p.count !== 1 ? 's' : ''} · ${p.out} out / ${p.in} in`}
                  style={{ fontSize: 10.5, fontWeight: 600, padding: '1px 7px', borderRadius: 20, background: '#eef2ff', color: '#4338ca', border: '1px solid #e0e7ff' }}>
                  {fmtNum(p.subNum)} · {p.count}
                </span>
              ))}
            </div>
            {leftover && <div style={{ marginTop: 3, fontSize: 10.5, color: '#94a3b8' }}>{leftover}</div>}
          </div>
        );
      })}
    </div>
  );
}

function DroppedCallPanel({ windowStartInput, setWindowStartInput, windowEndInput, setWindowEndInput, dispZone, analysis, names, onSelect }) {
  const hasResults = !!analysis && (analysis.dropped.length > 0 || analysis.newCommon.length > 0 || analysis.subjects.length > 0);
  const windowSet = windowStartInput.length > 0;

  const bestSwapForDropped = new Map();
  const bestSwapForNew = new Map();
  (analysis?.swaps || []).forEach(s => {
    if (!bestSwapForDropped.has(s.dropped.contact)) bestSwapForDropped.set(s.dropped.contact, s);
    if (!bestSwapForNew.has(s.added.contact)) bestSwapForNew.set(s.added.contact, s);
  });

  return (
    <div style={{ flex: 1, overflowY: 'auto', padding: 24 }}>
      <div style={{ maxWidth: 1100, margin: '0 auto' }}>
        <div style={{ marginBottom: 18 }}>
          <h3 style={{ margin: '0 0 4px', fontSize: 15, fontWeight: 700, color: '#0f172a' }}>Drop Phone Analysis<GeneratedMark /></h3>
          <p style={{ margin: '0 0 10px', fontSize: 12.5, color: '#64748b', maxWidth: 640, lineHeight: 1.5 }}>
            Finds numbers 2+ of your subjects dropped, paired with numbers 2+ of them picked up right
            after — a likely sign someone switched phones. Voice, SMS and MMS only.
          </p>
          <div style={{ display: 'flex', gap: 14, alignItems: 'center', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <span style={{ fontSize: 11, fontWeight: 600, color: '#64748b' }}>Transition start ({zoneAbbr(dispZone)})</span>
              <input type="datetime-local" value={windowStartInput} onChange={e => setWindowStartInput(e.target.value)}
                style={{ padding: '5px 8px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 12, outline: 'none' }} />
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <span style={{ fontSize: 11, fontWeight: 600, color: '#64748b' }} title="Leave blank to use start as a single cutoff instead of a window">End (optional)</span>
              <input type="datetime-local" value={windowEndInput} onChange={e => setWindowEndInput(e.target.value)}
                style={{ padding: '5px 8px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 12, outline: 'none' }} />
            </div>
          </div>
        </div>

        {!windowSet ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8', fontSize: 13, border: '1px dashed #e2e8f0', borderRadius: 10 }}>
            Set a start time to run the analysis. Add an end time too if the switch happened gradually
            across your subjects rather than all at once.
          </div>
        ) : !hasResults ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8', fontSize: 13, border: '1px dashed #e2e8f0', borderRadius: 10 }}>
            No matches for 2 or more subjects on both sides of that window.
          </div>
        ) : (
          <>
            {analysis.swaps.length > 0 && (
              <div style={{ marginBottom: 22 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#0f172a', marginBottom: 8 }}>🔁 Likely Number Swaps<GeneratedMark /></div>
                <div style={{ border: '1px solid #fde68a', borderRadius: 10, overflow: 'hidden' }}>
                  {analysis.swaps.map((s, i) => (
                    <div key={`${s.dropped.contact}-${s.added.contact}`} onClick={() => onSelect(s.added.contact)}
                      style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '10px 14px', borderBottom: i < analysis.swaps.length - 1 ? '1px solid #fef3c7' : 'none', background: '#fffbeb', cursor: 'pointer', flexWrap: 'wrap' }}
                      onMouseEnter={e => e.currentTarget.style.background = '#fef3c7'} onMouseLeave={e => e.currentTarget.style.background = '#fffbeb'}>
                      <span style={{ fontFamily: 'monospace', fontSize: 13, color: '#0f172a' }}>{fmtNum(s.dropped.contact)}</span>
                      <span style={{ color: '#b45309', fontWeight: 700 }}>→</span>
                      <span style={{ fontFamily: 'monospace', fontSize: 13, fontWeight: 700, color: '#0f172a' }}>{fmtNum(s.added.contact)}</span>
                      <span title={`${s.overlap.length} of ${s.unionSize} subjects involved show this pattern`}
                        style={{ fontSize: 10, fontWeight: 700, padding: '1px 7px', borderRadius: 20, background: '#fff', color: '#92400e', border: '1px solid #fde68a' }}>
                        {Math.round(s.confidence * 100)}%
                      </span>
                      <div style={{ marginLeft: 'auto', display: 'flex', gap: 5 }}>
                        {s.overlap.map(sub => (
                          <span key={sub} style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 20, background: '#fef3c7', color: '#92400e', border: '1px solid #fde68a' }}>{fmtNum(sub)}</span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
              <div style={{ flex: '1 1 460px', minWidth: 360 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#0f172a', marginBottom: 8 }}>📉 Dropped ({analysis.dropped.length})</div>
                <DroppedNumberList items={analysis.dropped} matches={bestSwapForDropped} names={names} onSelect={onSelect} />
              </div>
              <div style={{ flex: '1 1 460px', minWidth: 360 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#0f172a', marginBottom: 8 }}>📈 New in common ({analysis.newCommon.length})</div>
                <DroppedNumberList items={analysis.newCommon} matches={bestSwapForNew} names={names} onSelect={onSelect} />
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default function CDRReport({ data, onReset, resetLabel = 'New Upload', initialResponse = '' }) {
  const cdrData = data;
  const [selContact, setSelContact] = useState(null);
  const [search, setSearch]         = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [dirFilter, setDirFilter]   = useState('');
  const [page, setPage]             = useState(0);
  const [names, setNames]           = useState({});
  const [editingNum, setEditingNum] = useState(null);
  const [nameInput, setNameInput]   = useState('');
  const [showSubInfo, setShowSubInfo] = useState(false);
  const [showCommonCallers, setShowCommonCallers] = useState(false);
  const [showDroppedAnalysis, setShowDroppedAnalysis] = useState(false);
  const [showAnalysis, setShowAnalysis] = useState(false);
  const [windowStartInput, setWindowStartInput] = useState('');   // <input type="datetime-local"> values
  const [windowEndInput, setWindowEndInput]     = useState('');
  const [respFilter, setRespFilter] = useState(initialResponse);   // '' = all responses
  const [dedupeEnabled, setDedupeEnabled] = useState(true);
  const [srcZone, setSrcZone]       = useState('UTC');
  const [dispZone, setDispZone]     = useState('America/New_York');

  // Duplicates are flagged, not removed, at process time — this lets the
  // toggle apply instantly to every downstream view without reprocessing
  // the uploaded files.
  const baseRecords = useMemo(() => {
    const recs = cdrData?.records || [];
    return dedupeEnabled ? recs.filter(r => !r.isDuplicate) : recs;
  }, [cdrData, dedupeEnabled]);

  // Records for the active response (or all responses merged).
  const activeRecords = useMemo(() => {
    if (!respFilter) return baseRecords;
    return baseRecords.filter(r => `${r.carrier}:${r.subNum || 'unknown'}` === respFilter);
  }, [baseRecords, respFilter]);

  const stats = useMemo(
    () => activeRecords.length ? computeStats(activeRecords, srcZone, dispZone) : null,
    [activeRecords, srcZone, dispZone]
  );

  // Cross-subject analysis always runs over every uploaded number, regardless
  // of the active response filter — it only means something across the full set.
  const commonCallers = useMemo(() => computeCommonCallers(baseRecords), [baseRecords]);

  const windowStart = useMemo(() => pivotToRecordTime(windowStartInput, srcZone, dispZone), [windowStartInput, srcZone, dispZone]);
  const windowEnd   = useMemo(() => pivotToRecordTime(windowEndInput, srcZone, dispZone), [windowEndInput, srcZone, dispZone]);
  const droppedAnalysis = useMemo(
    () => windowStart ? computeDroppedCallAnalysis(baseRecords, windowStart.getTime(), windowEnd?.getTime()) : null,
    [baseRecords, windowStart, windowEnd]
  );

  // Record timestamps are wall-clock readings in the source zone; analysis works on real instants.
  const toEpoch = useCallback(ts => moment.tz(wallParts(ts), srcZone).valueOf(), [srcZone]);
  const carrierLabel = useCallback(c => CARRIERS[c]?.label || c, []);

  const selectCommonCaller = useCallback(num => {
    setShowCommonCallers(false); setShowDroppedAnalysis(false); setShowAnalysis(false); setRespFilter(''); setSelContact(num); setSearch(''); setPage(0);
  }, []);

  const filteredRecords = useMemo(() => {
    let r = activeRecords;
    if (selContact) r = r.filter(rec => rec.contactNumber === selContact);
    if (search) {
      const sn = search.replace(/\D/g, '');
      r = r.filter(rec => {
        const nameMatch = (names[rec.contactNumber] || '').toLowerCase().includes(search.toLowerCase());
        const numMatch  = sn ? rec.contactNumber.includes(sn) : false;
        return nameMatch || numMatch;
      });
    }
    if (typeFilter) r = r.filter(rec => rec.type === typeFilter);
    if (dirFilter)  r = r.filter(rec => rec.direction === dirFilter);
    return [...r].sort((a, b) => (b.timestamp?.getTime() || 0) - (a.timestamp?.getTime() || 0));
  }, [activeRecords, selContact, search, typeFilter, dirFilter, names]);

  const pageRecs   = filteredRecords.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);
  const totalPages = Math.ceil(filteredRecords.length / PAGE_SIZE);
  const label      = num => names[num] || fmtNum(num) || num;

  // Per-response counts/date-ranges are recomputed here (rather than baked
  // in at process time) so they reflect the dedupe toggle immediately too.
  const responses = useMemo(() => {
    return (cdrData?.responses || []).map(resp => {
      const recs = baseRecords.filter(r => r.carrier === resp.carrier && (r.subNum || 'unknown') === (resp.subNum || 'unknown'));
      const tsList = recs.map(r => r.timestamp).filter(Boolean);
      return {
        ...resp,
        count: recs.length,
        minDate: tsList.length ? new Date(Math.min(...tsList.map(t => t.getTime()))) : null,
        maxDate: tsList.length ? new Date(Math.max(...tsList.map(t => t.getTime()))) : null,
      };
    }).sort((a, b) => b.count - a.count);
  }, [cdrData, baseRecords]);
  const activeResp = respFilter ? responses.find(r => r.key === respFilter) : null;
  const activeCarriers = useMemo(() => [...new Set(activeRecords.map(r => r.carrier))], [activeRecords]);
  // Subscriber info: the selected response's, else (in "all" view) the first that has any.
  const subInfo  = activeResp?.subInfo || (!respFilter ? (responses.find(r => r.subInfo?.name)?.subInfo || null) : null);
  const rangeMin = activeResp?.minDate || cdrData?.minDate;
  const rangeMax = activeResp?.maxDate || cdrData?.maxDate;

  const exportCSV = () => {
    if (!filteredRecords.length) return;
    const hdrs = [`Timestamp (${dispZone})`, 'Carrier', 'Subscriber', 'Direction', 'Type', 'Contact Number', 'Contact Name', 'Duration (s)', 'Cell ID', 'IMEI'];
    const rows = filteredRecords.map(r => [
      fmtTs(r.timestamp, srcZone, dispZone), CARRIERS[r.carrier]?.label || r.carrier || '', fmtNum(r.subNum) || r.subNum || '',
      r.direction, r.type, r.contactNumber, names[r.contactNumber] || '', r.durationSeconds, r.cellId, r.imei,
    ]);
    const csv = [hdrs, ...rows].map(row => row.map(v => `"${String(v ?? '').replace(/"/g, '""')}"`).join(',')).join('\n');
    const a = Object.assign(document.createElement('a'), {
      href: URL.createObjectURL(new Blob([csv], { type: 'text/csv' })),
      download: 'phone-records-export.csv',
    });
    a.click(); URL.revokeObjectURL(a.href);
  };

  if (!stats || !cdrData) return null;
  return (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

          {/* Filter bar */}
          <div style={{ background: '#fff', borderBottom: '1px solid #e2e8f0', padding: '9px 18px', display: 'flex', gap: 10, alignItems: 'center', flexShrink: 0, flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: 11, color: '#64748b', fontWeight: 600 }}>Response:</span>
              <select value={respFilter} onChange={e => { setRespFilter(e.target.value); setPage(0); setSelContact(null); }}
                style={{ padding: '5px 8px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 12, cursor: 'pointer', outline: 'none', maxWidth: 280 }}>
                <option value=''>All responses ({responses.length})</option>
                {responses.map(r => (
                  <option key={r.key} value={r.key}>{CARRIERS[r.carrier]?.label || r.carrier} · {fmtNum(r.subNum) || r.subNum || 'unknown'} ({r.count})</option>
                ))}
              </select>
            </div>

            {activeCarriers.map(c => CARRIERS[c] && (
              <span key={c} style={{ fontSize: 11, fontWeight: 700, padding: '2px 9px', borderRadius: 20, background: CARRIERS[c].bg, color: CARRIERS[c].color, border: `1px solid ${CARRIERS[c].border}` }}>{CARRIERS[c].label}</span>
            ))}
            <span style={{ fontSize: 12, color: '#64748b' }}>{fmtDateRange(rangeMin, rangeMax, srcZone, dispZone)}</span>
            <span style={{ fontSize: 12, color: '#64748b' }}><span style={{ fontWeight: 600, color: '#0f172a' }}>{activeRecords.length.toLocaleString()}</span> records</span>
            {cdrData.duplicatesRemoved > 0 && (
              <label title="Identical records across overlapping returns — uncheck to show them again" style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, fontWeight: 600, padding: '2px 9px', borderRadius: 20, background: '#fef9c3', color: '#854d0e', border: '1px solid #fde68a', cursor: 'pointer' }}>
                <input type="checkbox" checked={dedupeEnabled} onChange={e => { setDedupeEnabled(e.target.checked); setPage(0); }} style={{ margin: 0, cursor: 'pointer' }} />
                {cdrData.duplicatesRemoved.toLocaleString()} duplicate{cdrData.duplicatesRemoved !== 1 ? 's' : ''} {dedupeEnabled ? 'removed' : 'found'}
              </label>
            )}

            {(subInfo?.name || subInfo?.accountNumber || subInfo?.payments?.length || subInfo?.notes?.length) && (
              <button onClick={() => setShowSubInfo(v => !v)} style={{ padding: '2px 9px', fontSize: 11, fontWeight: 700, borderRadius: 20, background: showSubInfo ? '#dcfce7' : '#f0fdf4', color: '#15803d', border: '1px solid #bbf7d0', cursor: 'pointer' }}>
                {showSubInfo ? 'Hide Info' : '👤 Subscriber Info'}
              </button>
            )}

            <button onClick={() => { setShowAnalysis(v => !v); setShowCommonCallers(false); setShowDroppedAnalysis(false); }} title="Pattern of life, incident window, contacts by day, handsets, cell sites, business numbers and a case summary PDF" style={{ padding: '2px 9px', fontSize: 11, fontWeight: 700, borderRadius: 20, background: showAnalysis ? '#bfdbfe' : '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe', cursor: 'pointer' }}>
              {showAnalysis ? 'Hide Analysis' : <>📊 Analysis<GeneratedMark /></>}
            </button>

            {commonCallers.subjects.length >= 2 && (
              <>
                <button onClick={() => { setShowCommonCallers(v => !v); setShowDroppedAnalysis(false); setShowAnalysis(false); }} style={{ padding: '2px 9px', fontSize: 11, fontWeight: 700, borderRadius: 20, background: showCommonCallers ? '#e0e7ff' : '#eef2ff', color: '#4338ca', border: '1px solid #e0e7ff', cursor: 'pointer' }}>
                  {showCommonCallers ? 'Hide Common Callers' : `🔗 Common Callers (${commonCallers.common.length})`}
                </button>
                <button onClick={() => { setShowDroppedAnalysis(v => !v); setShowCommonCallers(false); setShowAnalysis(false); }} style={{ padding: '2px 9px', fontSize: 11, fontWeight: 700, borderRadius: 20, background: showDroppedAnalysis ? '#fde68a' : '#fef3c7', color: '#92400e', border: '1px solid #fde68a', cursor: 'pointer' }}>
                  {showDroppedAnalysis ? 'Hide Drop Phone Analysis' : '📉 Drop Phone Analysis'}
                </button>
              </>
            )}

            <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }} title="How timestamps in the returns are stored">
                <span style={{ fontSize: 11, color: '#64748b', fontWeight: 600 }}>Source</span>
                <select value={srcZone} onChange={e => setSrcZone(e.target.value)} style={{ padding: '5px 6px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 12, cursor: 'pointer', outline: 'none' }}>
                  {TIME_ZONES.map(z => <option key={z.v} value={z.v}>{z.label}</option>)}
                </select>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }} title="Time zone to display records in">
                <span style={{ fontSize: 11, color: '#64748b', fontWeight: 600 }}>Show in</span>
                <select value={dispZone} onChange={e => setDispZone(e.target.value)} style={{ padding: '5px 6px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 12, cursor: 'pointer', outline: 'none' }}>
                  {TIME_ZONES.map(z => <option key={z.v} value={z.v}>{z.label}</option>)}
                </select>
              </div>
              <input value={search} onChange={e => { setSearch(e.target.value); setPage(0); }} placeholder="Search number or name…" style={{ padding: '5px 10px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 12, width: 170, outline: 'none' }} />
              <select value={typeFilter} onChange={e => { setTypeFilter(e.target.value); setPage(0); }} style={{ padding: '5px 8px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 12, cursor: 'pointer', outline: 'none' }}>
                <option value=''>All Types</option>
                <option value='voice'>Voice</option>
                <option value='sms'>SMS</option>
                <option value='mms'>MMS</option>
                <option value='data'>Data</option>
              </select>
              <select value={dirFilter} onChange={e => { setDirFilter(e.target.value); setPage(0); }} style={{ padding: '5px 8px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 12, cursor: 'pointer', outline: 'none' }}>
                <option value=''>All Directions</option>
                <option value='incoming'>Incoming</option>
                <option value='outgoing'>Outgoing</option>
              </select>
              {selContact && <button onClick={() => setSelContact(null)} style={{ padding: '5px 10px', background: '#fee2e2', color: '#991b1b', border: '1px solid #fca5a5', borderRadius: 6, fontSize: 12, cursor: 'pointer', fontWeight: 600, whiteSpace: 'nowrap' }}>✕ {label(selContact)}</button>}
              <button onClick={exportCSV} style={{ padding: '5px 12px', background: '#e0f2fe', color: '#0369a1', border: '1px solid #7dd3fc', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>Export CSV</button>
              <button onClick={onReset} style={{ padding: '5px 10px', background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 12, cursor: 'pointer', color: '#64748b' }}>{resetLabel}</button>
            </div>
          </div>

          {/* Subscriber Info Panel */}
          {showSubInfo && subInfo && (
            <div style={{ background: '#f0fdf4', borderBottom: '1px solid #bbf7d0', padding: '12px 20px', display: 'flex', gap: 36, flexWrap: 'wrap', flexShrink: 0 }}>
              {[
                { label: 'Name',        value: subInfo.name },
                { label: 'Account #',   value: subInfo.accountNumber },
                { label: 'SSN Last 4',  value: subInfo.ssn },
                { label: 'DOB',         value: subInfo.dob },
              ].map(({ label: l, value: v }) => v ? (
                <div key={l}>
                  <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em', color: '#15803d', marginBottom: 2 }}>{l}</div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#0f172a' }}>{v}</div>
                </div>
              ) : null)}
              {(subInfo.address || subInfo.city) && (
                <div>
                  <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em', color: '#15803d', marginBottom: 2 }}>Address</div>
                  <div style={{ fontSize: 13, color: '#0f172a' }}>{subInfo.address}</div>
                  <div style={{ fontSize: 12, color: '#475569' }}>{[subInfo.city, subInfo.state, subInfo.zip].filter(Boolean).join(', ')}</div>
                </div>
              )}
              {subInfo.billingParty && (subInfo.billingParty.name || subInfo.billingParty.accountNumber) && (
                <div>
                  <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em', color: '#15803d', marginBottom: 2 }}>Billing Party</div>
                  <div style={{ fontSize: 13, color: '#0f172a' }}>{subInfo.billingParty.name}</div>
                  <div style={{ fontSize: 12, color: '#475569' }}>{[subInfo.billingParty.street, subInfo.billingParty.city, subInfo.billingParty.state, subInfo.billingParty.zip].filter(Boolean).join(', ')}</div>
                  {subInfo.billingParty.status && <div style={{ fontSize: 11, color: '#475569' }}>{subInfo.billingParty.status}{subInfo.billingParty.billingCycle ? ` · Cycle ${subInfo.billingParty.billingCycle}` : ''}</div>}
                </div>
              )}
              {subInfo.userInfo && subInfo.userInfo.msisdn && (
                <div>
                  <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em', color: '#15803d', marginBottom: 2 }}>User (MSISDN)</div>
                  <div style={{ fontSize: 13, fontFamily: 'monospace', color: '#0f172a' }}>{fmtNum(subInfo.userInfo.msisdn)}</div>
                  {subInfo.userInfo.imsi && <div style={{ fontSize: 11, fontFamily: 'monospace', color: '#475569' }}>IMSI {subInfo.userInfo.imsi}</div>}
                  {subInfo.userInfo.name && subInfo.userInfo.name !== subInfo.name && <div style={{ fontSize: 12, color: '#0f172a' }}>{subInfo.userInfo.name}</div>}
                </div>
              )}
              {subInfo.imeis.length > 0 && (
                <div>
                  <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em', color: '#15803d', marginBottom: 2 }}>IMEI{subInfo.imeis.length > 1 ? 's' : ''}</div>
                  {subInfo.imeis.map((im, i) => <div key={i} style={{ fontSize: 12, fontFamily: 'monospace', color: '#0f172a' }}>{im}{subInfo.devices[i] ? ` — ${subInfo.devices[i]}` : ''}</div>)}
                </div>
              )}
              {subInfo.emails.length > 0 && (
                <div>
                  <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em', color: '#15803d', marginBottom: 2 }}>Email{subInfo.emails.length > 1 ? 's' : ''}</div>
                  {subInfo.emails.map((e, i) => <div key={i} style={{ fontSize: 12, color: '#0f172a' }}>{e}</div>)}
                </div>
              )}
              {subInfo.otherNumbers.length > 0 && (
                <div>
                  <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em', color: '#15803d', marginBottom: 2 }}>Other Numbers</div>
                  {subInfo.otherNumbers.map((n, i) => <div key={i} style={{ fontSize: 12, fontFamily: 'monospace', color: '#0f172a' }}>{fmtNum(n)}</div>)}
                </div>
              )}
              {subInfo.payments?.length > 0 && (
                <div>
                  <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em', color: '#15803d', marginBottom: 2 }}>Payments ({subInfo.payments.length})</div>
                  {subInfo.payments.slice(0, 5).map((p, i) => (
                    <div key={i} style={{ fontSize: 12, color: '#0f172a' }}>{p.postedDate} · {p.amount} · {p.method}</div>
                  ))}
                </div>
              )}
              {subInfo.notes?.length > 0 && (
                <div style={{ maxWidth: 340 }}>
                  <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em', color: '#15803d', marginBottom: 2 }}>Account Notes ({subInfo.notes.length})</div>
                  {subInfo.notes.slice(0, 3).map((n, i) => (
                    <div key={i} style={{ fontSize: 11.5, color: '#0f172a', marginBottom: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {n.timestamp ? fmtTs(n.timestamp, srcZone, dispZone) : '—'} · {n.text}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap', padding: '6px 18px', background: '#fffbeb', borderBottom: '1px solid #fde68a', fontSize: 12, color: '#92400e', flexShrink: 0 }}>
            <span><strong>Always refer to the actual returns from the carrier.</strong> This tool only helps you review the records; check anything you rely on against the original files.</span>
            <GeneratedLegend style={{ color: '#92400e' }}>Generated by LE Tech Tools (rankings and analysis). Not part of the original legal return.</GeneratedLegend>
          </div>

          {/* Body */}
          {showAnalysis ? (
            <PhoneAnalysis records={activeRecords} responses={responses} toEpoch={toEpoch} fmtNum={fmtNum} names={names}
              tz={dispZone} tzLabel={zoneAbbr(dispZone)} carrierLabel={carrierLabel}
              scope={activeResp ? `${carrierLabel(activeResp.carrier)} ${fmtNum(activeResp.subNum) || activeResp.subNum || 'unknown'}` : `All responses (${responses.length})`} />
          ) : showCommonCallers ? (
            <CommonCallersPanel data={commonCallers} names={names} srcZone={srcZone} dispZone={dispZone} onSelect={selectCommonCaller} />
          ) : showDroppedAnalysis ? (
            <DroppedCallPanel windowStartInput={windowStartInput} setWindowStartInput={setWindowStartInput} windowEndInput={windowEndInput} setWindowEndInput={setWindowEndInput} dispZone={dispZone} analysis={droppedAnalysis} names={names} onSelect={selectCommonCaller} />
          ) : (
          <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>

            {/* Sidebar */}
            <div style={{ width: 272, flexShrink: 0, borderRight: '1px solid #e2e8f0', background: '#fff', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              <div style={{ padding: '10px 14px', borderBottom: '1px solid #f1f5f9', display: 'flex', gap: 5, flexWrap: 'wrap' }}>
                {[
                  { label: 'Voice', val: stats.byType.voice, ...TYPE_COLORS.voice },
                  { label: 'SMS',   val: stats.byType.sms,   ...TYPE_COLORS.sms },
                  { label: 'MMS',   val: stats.byType.mms,   ...TYPE_COLORS.mms },
                  { label: 'Data',  val: stats.byType.data,  ...TYPE_COLORS.data },
                ].filter(x => x.val > 0).map(x => (
                  <span key={x.label} style={{ fontSize: 11, fontWeight: 700, padding: '3px 8px', borderRadius: 20, background: x.bg, color: x.color, border: `1px solid ${x.border}` }}>{x.label} {x.val.toLocaleString()}</span>
                ))}
              </div>
              <div style={{ padding: '10px 14px', borderBottom: '1px solid #f1f5f9' }}>
                <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', color: '#64748b', marginBottom: 6 }}>Activity by Hour</div>
                <BarChart values={stats.byHour} labels={HOURS} color='#bfdbfe' height={55} />
              </div>
              <div style={{ padding: '10px 14px', borderBottom: '1px solid #f1f5f9' }}>
                <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', color: '#64748b', marginBottom: 6 }}>Day of Week</div>
                <BarChart values={stats.byDow} labels={DAYS} color='#99f6e4' height={45} />
              </div>
              <div style={{ flex: 1, overflowY: 'auto' }}>
                <div style={{ padding: '8px 14px 6px', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', color: '#64748b', position: 'sticky', top: 0, background: '#fff', zIndex: 1, borderBottom: '1px solid #f1f5f9' }}>
                  Top Contacts ({stats.contacts.length})<GeneratedMark />
                </div>
                {stats.contacts.slice(0, 200).map((c, i) => {
                  const isSel = selContact === c.num, isEdit = editingNum === c.num;
                  return (
                    <div key={c.num} onClick={() => { if (!isEdit) { setSelContact(isSel ? null : c.num); setPage(0); } }} style={{ padding: '8px 14px', cursor: 'pointer', borderBottom: '1px solid #f8fafc', background: isSel ? '#e0f2fe' : 'transparent', transition: 'background .1s' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span style={{ fontSize: 10, color: '#94a3b8', width: 16, flexShrink: 0, textAlign: 'right' }}>{i + 1}</span>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          {isEdit ? (
                            <div onClick={e => e.stopPropagation()} style={{ display: 'flex', gap: 4 }}>
                              <input autoFocus value={nameInput} onChange={e => setNameInput(e.target.value)}
                                onKeyDown={e => { if (e.key === 'Enter') { setNames(p => ({ ...p, [c.num]: nameInput.trim() })); setEditingNum(null); } if (e.key === 'Escape') setEditingNum(null); }}
                                placeholder="Name…" style={{ flex: 1, padding: '2px 6px', border: '1px solid #0369a1', borderRadius: 4, fontSize: 12, outline: 'none' }} />
                              <button onClick={() => { setNames(p => ({ ...p, [c.num]: nameInput.trim() })); setEditingNum(null); }} style={{ padding: '2px 7px', background: '#0369a1', color: '#fff', border: 'none', borderRadius: 4, fontSize: 11, cursor: 'pointer' }}>✓</button>
                            </div>
                          ) : (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                              <div style={{ minWidth: 0, flex: 1 }}>
                                {names[c.num] && <div style={{ fontSize: 12, fontWeight: 700, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{names[c.num]}</div>}
                                <div style={{ fontSize: 11, color: names[c.num] ? '#64748b' : '#0f172a', fontFamily: 'monospace' }}>{fmtNum(c.num)}</div>
                              </div>
                              <button onClick={e => { e.stopPropagation(); setEditingNum(c.num); setNameInput(names[c.num] || ''); }} style={{ color: '#94a3b8', background: 'transparent', border: 'none', cursor: 'pointer', padding: '0 2px', fontSize: 12, flexShrink: 0 }} title="Assign name">✎</button>
                            </div>
                          )}
                        </div>
                        <span style={{ fontSize: 12, fontWeight: 700, color: isSel ? '#0369a1' : '#0f172a', flexShrink: 0 }}>{c.count}</span>
                      </div>
                      {(c.in + c.out) > 0 && (
                        <div style={{ marginTop: 4, marginLeft: 22, height: 3, borderRadius: 2, background: '#f1f5f9', overflow: 'hidden' }}>
                          <div style={{ height: '100%', width: `${(c.out / c.count) * 100}%`, background: '#818cf8', borderRadius: 2 }} title={`${c.out} out / ${c.in} in`} />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Records table */}
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              <div style={{ padding: '7px 16px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
                <span style={{ fontSize: 13, color: '#64748b' }}>
                  <span style={{ fontWeight: 600, color: '#0f172a' }}>{filteredRecords.length.toLocaleString()}</span> records
                  {selContact && <span style={{ color: '#0369a1', marginLeft: 6 }}>· {label(selContact)}</span>}
                </span>
                {totalPages > 1 && (
                  <div style={{ marginLeft: 'auto', display: 'flex', gap: 6, alignItems: 'center' }}>
                    <button disabled={page === 0} onClick={() => setPage(p => p - 1)} style={{ padding: '3px 10px', border: '1px solid #e2e8f0', borderRadius: 4, fontSize: 12, cursor: page === 0 ? 'default' : 'pointer', background: '#fff', color: page === 0 ? '#cbd5e1' : '#0f172a' }}>←</button>
                    <span style={{ fontSize: 12, color: '#64748b' }}>{page + 1} / {totalPages}</span>
                    <button disabled={page === totalPages - 1} onClick={() => setPage(p => p + 1)} style={{ padding: '3px 10px', border: '1px solid #e2e8f0', borderRadius: 4, fontSize: 12, cursor: page === totalPages - 1 ? 'default' : 'pointer', background: '#fff', color: page === totalPages - 1 ? '#cbd5e1' : '#0f172a' }}>→</button>
                  </div>
                )}
              </div>
              <div style={{ flex: 1, overflowY: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                    <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                      {[`Timestamp (${zoneAbbr(dispZone)})`, 'Type', 'Dir', 'Contact', 'Duration', 'Carrier / File'].map((h, i) => (
                        <th key={h} style={{ padding: '8px ' + (i === 0 ? '14px' : '10px'), textAlign: i === 4 ? 'right' : 'left', fontWeight: 700, fontSize: 11, color: '#64748b', textTransform: 'uppercase', letterSpacing: '.05em', whiteSpace: 'nowrap' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {pageRecs.length === 0 ? (
                      <tr><td colSpan={6} style={{ padding: 40, textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>No records match the current filters.</td></tr>
                    ) : pageRecs.map(r => {
                      const tc = TYPE_COLORS[r.type] || TYPE_COLORS.unknown;
                      return (
                        <tr key={r.id} style={{ borderBottom: '1px solid #f1f5f9' }} onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'} onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                          <td style={{ padding: '7px 14px', whiteSpace: 'nowrap', fontSize: 12, color: '#475569', fontFamily: 'monospace' }}>{r.timestamp ? fmtTs(r.timestamp, srcZone, dispZone) : '—'}</td>
                          <td style={{ padding: '7px 10px' }}>
                            <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 6px', borderRadius: 20, background: tc.bg, color: tc.color, border: `1px solid ${tc.border}`, textTransform: 'uppercase', letterSpacing: '.04em' }}>
                              {r.type === 'unknown' ? '?' : r.type}
                            </span>
                          </td>
                          <td style={{ padding: '7px 10px', fontSize: 13 }}>
                            <span style={{ fontWeight: 700, color: r.direction === 'outgoing' ? '#6d28d9' : r.direction === 'incoming' ? '#166534' : '#94a3b8' }}>
                              {r.direction === 'outgoing' ? '↑' : r.direction === 'incoming' ? '↓' : '–'}
                            </span>
                          </td>
                          <td style={{ padding: '7px 10px', maxWidth: 220 }}>
                            <div onClick={() => { if (r.contactNumber) { setSelContact(selContact === r.contactNumber ? null : r.contactNumber); setPage(0); } }} style={{ cursor: r.contactNumber ? 'pointer' : 'default' }}>
                              {names[r.contactNumber] && <div style={{ fontSize: 12, fontWeight: 700, color: '#0f172a' }}>{names[r.contactNumber]}</div>}
                              <div style={{ fontSize: 12, color: names[r.contactNumber] ? '#64748b' : '#0f172a', fontFamily: 'monospace' }}>{fmtNum(r.contactNumber) || r.contactNumber || '—'}</div>
                            </div>
                          </td>
                          <td style={{ padding: '7px 10px', textAlign: 'right', fontSize: 12, color: '#64748b', fontFamily: 'monospace', whiteSpace: 'nowrap' }}>
                            {r.durationSeconds > 0 ? fmtDur(r.durationSeconds) : '—'}
                          </td>
                          <td title={r.sourceFile || ''} style={{ padding: '7px 10px', fontSize: 11, color: '#94a3b8', maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {[CARRIERS[r.carrier]?.label, r.sourceFile].filter(Boolean).join(' · ') || '—'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          )}
        </div>
  );
}
